import React, { useCallback, useMemo } from "react";
import { useApolloClient } from "@apollo/client";
import { renderToStaticMarkup } from "react-dom/server";
import { AgGrid, AgGridReact, Flex, Grid } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { DEFAULT_PAGE_SIZE } from "../../constants";
import { usePaymentsContext } from "../Context/PaymentsContext";
import AmountRefPreserver from "./amountRefPreserver";
import { getColumnDefinitions } from "./Factories/columnDefinitionFactory";
import { sideBarDefs } from "./Factories/sidebarFactory";
import { PaymentBatchesGraphqlDataSource } from "./paymentBatchesGQLDataSource";
import { useTableRefreshFromSignal } from "./refreshSignal";
import { paymentsTableContainer } from "./styles_vanilla.css";
import { PaymentRowItem } from "./types";

export type PaymentBatchesTableProps = {
  onBatchSelected?: (paymentRowItem?: PaymentRowItem) => void;
};

/**
 * Renders the AG-Grid table with the needed information for the Payment Batches
 * @returns PaymentBatchesTable
 */
const PaymentBatchesTable: React.FC<PaymentBatchesTableProps> = ({ onBatchSelected }) => {
  const noRowsTemplate = `<span>No batches found</span>`;
  const client = useApolloClient();
  const { isGPX, isM3Enabled } = usePaymentsContext();
  const amountRefPreserver = useMemo(() => new AmountRefPreserver(), []);

  const gridDataSource = useMemo<AgGrid.IServerSideDatasource>(() => {
    return new PaymentBatchesGraphqlDataSource(client, amountRefPreserver, isGPX, isM3Enabled);
  }, [client, amountRefPreserver, isGPX, isM3Enabled]);

  const sideBar = useMemo(
    () => sideBarDefs({ isGPX, isM3Enabled, amountRefPreserver }),
    [isGPX, isM3Enabled, amountRefPreserver],
  );

  const icons = useMemo(
    () => ({ export: renderToStaticMarkup(<Icon type="file-download" />) }),
    [],
  );

  const { onGridReady } = useTableRefreshFromSignal();

  const onRowClicked = useCallback(
    (event: AgGrid.RowClickedEvent<PaymentRowItem>) => {
      const rowData = event.data;
      if (rowData) {
        onBatchSelected?.(rowData);
      }
    },
    [onBatchSelected],
  );

  const gridOptions: AgGridReact.AgGridReactProps["gridOptions"] = {
    suppressCellFocus: true,
    rowHeight: 50,
    suppressRowClickSelection: true,
    suppressContextMenu: true,
    onRowClicked,
  };

  const defaultColumnOptions = {
    editable: false,
    flex: 1,
    minWidth: 60,
    suppressHeaderMenuButton: false,
    enableCellChangeFlash: true,
  };

  const onFilterChanged = (event: AgGrid.FilterChangedEvent) => {
    // De-select all rows when the filter changes
    event.api.deselectAll();
  };

  return (
    <Flex className={paymentsTableContainer}>
      <Grid<PaymentRowItem>
        cacheBlockSize={DEFAULT_PAGE_SIZE}
        columnDefs={getColumnDefinitions(isGPX, isM3Enabled)}
        defaultColDef={defaultColumnOptions}
        getRowId={(params) => params.data.id}
        gridId="payment_batches"
        gridOptions={gridOptions}
        icons={icons}
        onFilterChanged={onFilterChanged}
        onGridReady={onGridReady}
        overlayNoRowsTemplate={noRowsTemplate}
        pagination
        paginationPageSize={DEFAULT_PAGE_SIZE}
        paginationPageSizeSelector={false}
        reactiveCustomComponents
        rowModelType="serverSide"
        rowSelection="single"
        serverSideDatasource={gridDataSource}
        sideBar={sideBar}
        suppressServerSideFullWidthLoadingRow
      />
    </Flex>
  );
};

export { PaymentBatchesTable };
